import React, { useState } from 'react';
import { RiAddLine, RiStackLine, RiTeamLine } from 'react-icons/ri';
import styles from '../../styles/AdminPage.module.css';
import { buildAgentCards, buildUsageTotals } from '../../utils/agentAnalytics';

const IconH4 = ({ icon: Icon, children }) => (
  <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <Icon style={{ flexShrink: 0 }} />
    {children}
  </h4>
);

const AdminTable = ({
  sortedUsers,
  user,
  sortColumn,
  sortDirection,
  handleSort,
  toggleUserDetails,
  expandedUser,
  handleApprovalToggle,
  handleAdminToggle,
  handleDeleteUser,
  actionLoading,
  loadingAnalytics,
  analyticsData,
  analyticsPeriod,
  analyticsMonths,
  handlePeriodChange,
  handleRebuildAnalytics,
  swarmAnalyticsData,
  workflowAnalyticsData,
}) => {
  // Tracks which year is selected in the year dropdown, per user
  const [selectedYears, setSelectedYears] = useState({});
  // Tracks which analytics view is active per user: 'all' | 'orchestration' | 'swarm' | 'workflow'
  const [viewModes, setViewModes] = useState({});
  // User whose profile popup is open (full user object)
  const [profilePopup, setProfilePopup] = useState(null);

  const fmtDate = (v) =>
    v
      ? new Date(v).toLocaleString('en-US', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: false,
        })
      : '—';

  return (
    <>
    <div className={styles.userTable}>
      <table>
        <thead>
          <tr>
            <th style={{ width: '40px' }}></th>
            <th className={styles.sortableHeader} onClick={() => handleSort('name')} title="Click to sort by name">
              Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th className={styles.sortableHeader} onClick={() => handleSort('last_login')} title="Click to sort by last login">
              Last Login {sortColumn === 'last_login' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th className={styles.sortableHeader} onClick={() => handleSort('last_swarm')} title="Click to sort by last swarm">
              Last Swarm {sortColumn === 'last_swarm' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th className={styles.sortableHeader} onClick={() => handleSort('last_query')} title="Click to sort by last orchestration">
              Last Orchestration {sortColumn === 'last_query' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th className={styles.sortableHeader} onClick={() => handleSort('last_workflow')} title="Click to sort by last workflow">
              Last Workflow {sortColumn === 'last_workflow' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th className={styles.sortableHeader} onClick={() => handleSort('status')} title="Click to sort by approval status">
              Status {sortColumn === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedUsers.length === 0 ? (
            <tr>
              <td colSpan="8" className={styles.emptyState}>
                No users found
              </td>
            </tr>
          ) : (
            sortedUsers.map((u) => {
              const viewMode = viewModes[u.id] || 'all';
              const showOrch = viewMode === 'all' || viewMode === 'orchestration';
              const showSwarm = viewMode === 'all' || viewMode === 'swarm';
              const showWorkflow = viewMode === 'all' || viewMode === 'workflow';
              const showGrandTotal = viewMode === 'grand-total' || viewMode === 'all';
              const showAgents = showOrch || showWorkflow;

              // Pre-compute merged grand totals (safe when either source is absent)
              const _orchData = analyticsData[u.id];
              const _swarmData = swarmAnalyticsData?.[u.id];
              const _wfData = workflowAnalyticsData?.[u.id];

              // Unified agent cards must be after _wfData is defined
              const agentCards = buildAgentCards(analyticsData[u.id], _wfData);
              const _orchTok = _orchData?.tokens || { input: 0, output: 0, total: 0 };
              const _swarmTok = _swarmData?.tokens || { input: 0, output: 0, total: 0 };
              const _wfTok = _wfData?.tokens || { input: 0, output: 0, total: 0 };
              const grandTokens = {
                input: _orchTok.input + _swarmTok.input + _wfTok.input,
                output: _orchTok.output + _swarmTok.output + _wfTok.output,
                total: _orchTok.total + _swarmTok.total + _wfTok.total,
              };
              const { models: grandModels, providers: grandProviders } = buildUsageTotals(
                _orchData,
                _swarmData,
                _wfData,
              );

              return (
              <React.Fragment key={u.id}>
                <tr className={u.id === user.id ? styles.currentUser : ''} style={{ height: '120px' }}>
                  <td>
                    <button onClick={() => toggleUserDetails(u.id)} className={styles.expandButton} title="View usage details">
                      {expandedUser === u.id ? '▼' : '▶'}
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => setProfilePopup(u)}
                      style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        color: 'var(--link-color, #4a9eff)', textDecoration: 'underline',
                        fontWeight: 500, textAlign: 'left',
                      }}
                      title="Click to view profile details"
                    >
                      {u.name || 'N/A'}
                    </button>
                    {!!u.is_admin && <span className={styles.adminBadge}>Admin</span>}
                    {u.id === user.id && <span className={styles.youBadge}>You</span>}
                  </td>
                  <td>{fmtDate(u.last_login)}</td>
                  <td>{fmtDate(u.last_swarm)}</td>
                  <td>{fmtDate(u.last_query)}</td>
                  <td>{fmtDate(u.last_workflow)}</td>
                  <td>
                    <span className={u.approved ? styles.statusApproved : styles.statusPending}>
                      {u.approved ? 'Approved' : 'Pending'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions} style={{ flexDirection: 'column', gap: '4px' }}>
                      <button
                        onClick={() => handleApprovalToggle(u.id, u.approved)}
                        disabled={actionLoading[u.id] === 'approve'}
                        className={u.approved ? styles.revokeButton : styles.approveButton}
                        style={{ width: '100%' }}
                      >
                        {actionLoading[u.id] === 'approve' ? '...' : u.approved ? 'Revoke' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleAdminToggle(u.id, u.is_admin)}
                        disabled={actionLoading[u.id] === 'admin' || u.id === user.id || !u.is_admin}
                        className={u.is_admin ? styles.removeAdminButton : styles.makeAdminButton}
                        title={
                          !u.is_admin
                            ? 'Make Admin is currently disabled'
                            : u.id === user.id
                            ? 'Cannot modify your own admin status'
                            : 'Remove admin privileges'
                        }
                        style={{ width: '100%' }}
                      >
                        {actionLoading[u.id] === 'admin' ? '...' : u.is_admin ? 'Remove Admin' : 'Make Admin'}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(u.id, u.name)}
                        disabled={actionLoading[u.id] === 'delete' || u.id === user.id}
                        className={styles.deleteButton}
                        style={{ width: '100%' }}
                      >
                        {actionLoading[u.id] === 'delete' ? '...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Expandable Analytics Row */}
                {expandedUser === u.id && (
                  <tr className={styles.analyticsRow}>
                    <td colSpan="8">
                      <div className={styles.analyticsContent}>
                        {loadingAnalytics[u.id] ? (
                          <div className={styles.analyticsLoading}>Loading usage data...</div>
                        ) : analyticsData[u.id] ? (
                          <>
                            <div className={styles.analyticsHeader}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <h3 style={{ margin: 0 }}>
                                  Usage Analytics
                                  <span className={styles.analyticsNote}>
                                    {analyticsPeriod[u.id] === 'lifetime' || !analyticsPeriod[u.id]
                                      ? '📊 Lifetime cumulative totals'
                                      : `📅 ${
                                          analyticsMonths[u.id]?.find(
                                            (m) => `${m.year}-${String(m.month).padStart(2, '0')}` === analyticsPeriod[u.id]
                                          )?.label || analyticsPeriod[u.id]
                                        }`}
                                  </span>
                                </h3>
                                <select
                                  value={viewMode}
                                  onChange={(e) => setViewModes(prev => ({ ...prev, [u.id]: e.target.value }))}
                                  style={{
                                    padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem',
                                    border: '1px solid var(--border-color, #ccc)',
                                    background: 'var(--input-background, #fff)',
                                    color: 'var(--text-color, #333)', cursor: 'pointer',
                                  }}
                                >
                                  <option value="all">All</option>
                                  <option value="grand-total">Grand Total</option>
                                  <option value="orchestration">Orchestration</option>
                                  <option value="swarm">Swarm</option>
                                  <option value="workflow">Workflow</option>
                                </select>
                              </div>

                              {/* Period selector */}
                              {(() => {
                                const months = analyticsMonths[u.id] || [];
                                // Group months by year, sorted most-recent-first within each year
                                const monthsByYear = months.reduce((acc, m) => {
                                  const y = String(m.year);
                                  if (!acc[y]) acc[y] = [];
                                  acc[y].push(m);
                                  return acc;
                                }, {});
                                const availableYears = Object.keys(monthsByYear).sort((a, b) => b - a);
                                // Derive selected year: from current period string, or most recent year
                                const currentPeriod = analyticsPeriod[u.id];
                                const derivedYear = currentPeriod && currentPeriod !== 'lifetime'
                                  ? currentPeriod.split('-')[0]
                                  : availableYears[0];
                                const activeYear = selectedYears[u.id] || derivedYear;
                                const monthsForYear = (monthsByYear[activeYear] || [])
                                  .slice()
                                  .sort((a, b) => b.month - a.month);
                                const activeMonthKey = currentPeriod && currentPeriod !== 'lifetime' && currentPeriod.startsWith(activeYear)
                                  ? currentPeriod : '';
                                const selectStyle = {
                                  padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem',
                                  border: '1px solid var(--border-color, #ccc)',
                                  background: 'var(--input-background, #fff)',
                                  color: 'var(--text-color, #333)', cursor: 'pointer',
                                };
                                return (
                                  <div className={styles.periodSelector}>
                                    <button
                                      className={!currentPeriod || currentPeriod === 'lifetime'
                                        ? styles.periodButtonActive : styles.periodButton}
                                      onClick={() => handlePeriodChange(u.id, 'lifetime')}
                                    >
                                      Lifetime
                                    </button>
                                    {availableYears.length > 0 && (
                                      <select
                                        value={activeYear || ''}
                                        style={selectStyle}
                                        onChange={(e) => {
                                          const year = e.target.value;
                                          setSelectedYears(prev => ({ ...prev, [u.id]: year }));
                                          // Auto-select the most recent month of the new year
                                          const first = (monthsByYear[year] || [])
                                            .slice().sort((a, b) => b.month - a.month)[0];
                                          if (first) {
                                            handlePeriodChange(u.id, `${first.year}-${String(first.month).padStart(2, '0')}`);
                                          }
                                        }}
                                      >
                                        {availableYears.map(y => (
                                          <option key={y} value={y}>{y}</option>
                                        ))}
                                      </select>
                                    )}
                                    {monthsForYear.length > 0 && (
                                      <select
                                        value={activeMonthKey}
                                        style={selectStyle}
                                        onChange={(e) => handlePeriodChange(u.id, e.target.value)}
                                      >
                                        <option value="" disabled>Month</option>
                                        {monthsForYear.map(m => {
                                          const key = `${m.year}-${String(m.month).padStart(2, '0')}`;
                                          return <option key={key} value={key}>{m.label}</option>;
                                        })}
                                      </select>
                                    )}
                                  </div>
                                );
                              })()}

                              <div className={styles.analyticsHeaderButtons}>
                                <button
                                  className={styles.refreshAnalyticsButton}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                  }}
                                  title="Refresh analytics for this user"
                                  disabled={actionLoading[u.id] === 'rebuild'}
                                >
                                  🔄 Refresh
                                </button>
                                <button
                                  className={styles.rebuildAnalyticsButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRebuildAnalytics(u.id, u.name);
                                  }}
                                  disabled={actionLoading[u.id] === 'rebuild'}
                                  title="Rebuild analytics from scratch by re-parsing all chat histories"
                                >
                                  {actionLoading[u.id] === 'rebuild' ? '⏳ Rebuilding...' : '🔨 Rebuild'}
                                </button>
                              </div>
                            </div>
                            <div className={styles.analyticsGrid}>
                              {showGrandTotal && <>
                                <div className={styles.analyticsSection}>
                                  <IconH4 icon={RiAddLine}>Activity</IconH4>
                                  <div className={styles.analyticsStats}>
                                    <div className={styles.stat}>
                                      <span className={styles.statLabel}>Ask Queries:</span>
                                      <span className={styles.statValue}>{_orchData?.total_queries ?? 0}</span>
                                    </div>
                                    <div className={styles.stat}>
                                      <span className={styles.statLabel}>Ask Conversations:</span>
                                      <span className={styles.statValue}>{_orchData?.total_conversations ?? 0}</span>
                                    </div>
                                    <div className={styles.stat}>
                                      <span className={styles.statLabel}>Web Searches:</span>
                                      <span className={styles.statValue}>{_orchData?.web_search_count ?? 0}</span>
                                    </div>
                                    <div className={styles.stat}>
                                      <span className={styles.statLabel}>Swarm Sessions:</span>
                                      <span className={styles.statValue}>{_swarmData?.total_sessions ?? 0}</span>
                                    </div>
                                    <div className={styles.stat}>
                                      <span className={styles.statLabel}>Swarm Turns:</span>
                                      <span className={styles.statValue}>{_swarmData?.total_turns ?? 0}</span>
                                    </div>
                                    <div className={styles.stat}>
                                      <span className={styles.statLabel}>Workflow Runs:</span>
                                      <span className={styles.statValue}>{_wfData?.total_runs ?? 0}</span>
                                    </div>
                                    <div className={styles.stat}>
                                      <span className={styles.statLabel}>Workflow Completed:</span>
                                      <span className={styles.statValue}>{_wfData?.completed_runs ?? 0}</span>
                                    </div>
                                  </div>
                                </div>

                                <div className={styles.analyticsSection}>
                                  <IconH4 icon={RiAddLine}>Total Token Usage</IconH4>
                                  <div className={styles.analyticsStats}>
                                    <div className={styles.stat}>
                                      <span className={styles.statLabel}>Input Tokens:</span>
                                      <span className={styles.statValue}>{grandTokens.input.toLocaleString()}</span>
                                    </div>
                                    <div className={styles.stat}>
                                      <span className={styles.statLabel}>Output Tokens:</span>
                                      <span className={styles.statValue}>{grandTokens.output.toLocaleString()}</span>
                                    </div>
                                    <div className={styles.stat} style={{ background: 'rgba(74,158,255,0.1)', borderRadius: '4px', fontWeight: 600 }}>
                                      <span className={styles.statLabel}>Total Tokens:</span>
                                      <span className={styles.statValue}>{grandTokens.total.toLocaleString()}</span>
                                    </div>
                                    <div className={styles.stat}>
                                      <span className={styles.statLabel}>Orch Tokens:</span>
                                      <span className={styles.statValue}>{_orchTok.total.toLocaleString()}</span>
                                    </div>
                                    <div className={styles.stat}>
                                      <span className={styles.statLabel}>Swarm Tokens:</span>
                                      <span className={styles.statValue}>{_swarmTok.total.toLocaleString()}</span>
                                    </div>
                                    <div className={styles.stat}>
                                      <span className={styles.statLabel}>Workflow Tokens:</span>
                                      <span className={styles.statValue}>{_wfTok.total.toLocaleString()}</span>
                                    </div>
                                  </div>
                                </div>

                                {Object.keys(grandModels).length > 0 && (
                                  <div className={styles.analyticsSection}>
                                    <IconH4 icon={RiAddLine}>Models Used</IconH4>
                                    <div className={styles.modelList}>
                                      {Object.entries(grandModels)
                                        .sort(([, a], [, b]) => b - a)
                                        .map(([model, count]) => (
                                          <div key={model} className={styles.modelItem}>
                                            <span className={styles.modelName}>{model}</span>
                                            <span className={styles.modelCount}>{count}</span>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}

                                {Object.keys(grandProviders).length > 0 && (
                                  <div className={styles.analyticsSection}>
                                    <IconH4 icon={RiAddLine}>Providers</IconH4>
                                    <div className={styles.providerList}>
                                      {Object.entries(grandProviders)
                                        .sort(([, a], [, b]) => b - a)
                                        .map(([provider, count]) => (
                                          <div key={provider} className={styles.providerItem}>
                                            <span className={styles.providerName}>{provider}</span>
                                            <span className={styles.providerCount}>{count}</span>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                              </>}

                              {showOrch && <>
                              <div className={styles.analyticsSection}>
                                <IconH4 icon={RiStackLine}>Ask Overview</IconH4>
                                <div className={styles.analyticsStats}>
                                  <div className={styles.stat}>
                                    <span className={styles.statLabel}>Total Queries:</span>
                                    <span className={styles.statValue}>{analyticsData[u.id].total_queries}</span>
                                  </div>
                                  <div className={styles.stat}>
                                    <span className={styles.statLabel}>Conversations:</span>
                                    <span className={styles.statValue}>{analyticsData[u.id].total_conversations}</span>
                                  </div>
                                  <div className={styles.stat}>
                                    <span className={styles.statLabel}>Web Searches:</span>
                                    <span className={styles.statValue}>{analyticsData[u.id].web_search_count}</span>
                                  </div>
                                  <div className={styles.stat}>
                                    <span className={styles.statLabel}>Concise Only:</span>
                                    <span className={styles.statValue}>{analyticsData[u.id].concise_calls ?? 0}</span>
                                  </div>
                                </div>
                              </div>

                              <div className={styles.analyticsSection}>
                                <IconH4 icon={RiStackLine}>Agent Usage</IconH4>
                                <div className={styles.analyticsStats}>
                                  <div className={styles.stat}>
                                    <span className={styles.statLabel}>Generalist:</span>
                                    <span className={styles.statValue}>{analyticsData[u.id].agent_calls.generalist}</span>
                                  </div>
                                  <div className={styles.stat}>
                                    <span className={styles.statLabel}>Researcher:</span>
                                    <span className={styles.statValue}>{analyticsData[u.id].agent_calls.researcher}</span>
                                  </div>
                                  <div className={styles.stat}>
                                    <span className={styles.statLabel}>Evaluator:</span>
                                    <span className={styles.statValue}>{analyticsData[u.id].agent_calls.evaluator ?? 0}</span>
                                  </div>
                                </div>
                              </div>

                              <div className={styles.analyticsSection}>
                                <IconH4 icon={RiStackLine}>Ask Usage</IconH4>
                                <div className={styles.analyticsStats}>
                                  <div className={styles.stat}>
                                    <span className={styles.statLabel}>Input Tokens:</span>
                                    <span className={styles.statValue}>{analyticsData[u.id].tokens.input.toLocaleString()}</span>
                                  </div>
                                  <div className={styles.stat}>
                                    <span className={styles.statLabel}>Output Tokens:</span>
                                    <span className={styles.statValue}>{analyticsData[u.id].tokens.output.toLocaleString()}</span>
                                  </div>
                                  <div className={styles.stat} style={{ background: 'rgba(74,158,255,0.1)', borderRadius: '4px', fontWeight: 600 }}>
                                    <span className={styles.statLabel}>Total Tokens:</span>
                                    <span className={styles.statValue}>{analyticsData[u.id].tokens.total.toLocaleString()}</span>
                                  </div>
                                  <div className={styles.stat}>
                                    <span className={styles.statLabel}>Router In:</span>
                                    <span className={styles.statValue}>{(analyticsData[u.id].router_tokens?.input ?? 0).toLocaleString()}</span>
                                  </div>
                                  <div className={styles.stat}>
                                    <span className={styles.statLabel}>Router Out:</span>
                                    <span className={styles.statValue}>{(analyticsData[u.id].router_tokens?.output ?? 0).toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>

                              <div className={styles.analyticsSection}>
                                <IconH4 icon={RiStackLine}>Ask Models</IconH4>
                                <div className={styles.modelList}>
                                  {Object.entries(analyticsData[u.id].model_usage).map(([model, count]) => (
                                    <div key={model} className={styles.modelItem}>
                                      <span className={styles.modelName}>{model}</span>
                                      <span className={styles.modelCount}>{count}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className={styles.analyticsSection}>
                                <IconH4 icon={RiStackLine}>Ask Providers</IconH4>
                                <div className={styles.providerList}>
                                  {Object.entries(analyticsData[u.id].provider_usage).map(([provider, count]) => (
                                    <div key={provider} className={styles.providerItem}>
                                      <span className={styles.providerName}>{provider}</span>
                                      <span className={styles.providerCount}>{count}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              </>}

                              {showSwarm && swarmAnalyticsData && swarmAnalyticsData[u.id] && swarmAnalyticsData[u.id].total_sessions > 0 && (
                                <>
                                  <div className={styles.analyticsSection}>
                                    <IconH4 icon={RiTeamLine}>Swarm Overview</IconH4>
                                    <div className={styles.analyticsStats}>
                                      <div className={styles.stat}>
                                        <span className={styles.statLabel}>Total Sessions:</span>
                                        <span className={styles.statValue}>{swarmAnalyticsData[u.id].total_sessions}</span>
                                      </div>
                                      <div className={styles.stat}>
                                        <span className={styles.statLabel}>Completed:</span>
                                        <span className={styles.statValue}>{swarmAnalyticsData[u.id].completed}</span>
                                      </div>
                                      <div className={styles.stat}>
                                        <span className={styles.statLabel}>Stopped:</span>
                                        <span className={styles.statValue}>{swarmAnalyticsData[u.id].stopped}</span>
                                      </div>
                                      <div className={styles.stat}>
                                        <span className={styles.statLabel}>Total Turns:</span>
                                        <span className={styles.statValue}>{swarmAnalyticsData[u.id].total_turns}</span>
                                      </div>
                                    </div>
                                  </div>

                                  {swarmAnalyticsData[u.id].tokens && swarmAnalyticsData[u.id].tokens.total > 0 && (
                                    <div className={styles.analyticsSection}>
                                      <IconH4 icon={RiTeamLine}>Swarm Usage</IconH4>
                                      <div className={styles.analyticsStats}>
                                        <div className={styles.stat}>
                                          <span className={styles.statLabel}>Input Tokens:</span>
                                          <span className={styles.statValue}>{swarmAnalyticsData[u.id].tokens.input.toLocaleString()}</span>
                                        </div>
                                        <div className={styles.stat}>
                                          <span className={styles.statLabel}>Output Tokens:</span>
                                          <span className={styles.statValue}>{swarmAnalyticsData[u.id].tokens.output.toLocaleString()}</span>
                                        </div>
                                        <div className={styles.stat} style={{ background: 'rgba(74,158,255,0.1)', borderRadius: '4px', fontWeight: 600 }}>
                                          <span className={styles.statLabel}>Total Tokens:</span>
                                          <span className={styles.statValue}>{swarmAnalyticsData[u.id].tokens.total.toLocaleString()}</span>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {swarmAnalyticsData[u.id].model_usage && Object.keys(swarmAnalyticsData[u.id].model_usage).length > 0 && (
                                    <div className={styles.analyticsSection}>
                                      <IconH4 icon={RiTeamLine}>Swarm Models</IconH4>
                                      <div className={styles.modelList}>
                                        {Object.entries(swarmAnalyticsData[u.id].model_usage).map(([model, count]) => (
                                          <div key={model} className={styles.modelItem}>
                                            <span className={styles.modelName}>{model}</span>
                                            <span className={styles.modelCount}>{count}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {swarmAnalyticsData[u.id].provider_usage && Object.keys(swarmAnalyticsData[u.id].provider_usage).length > 0 && (
                                    <div className={styles.analyticsSection}>
                                      <IconH4 icon={RiTeamLine}>Swarm Providers</IconH4>
                                      <div className={styles.providerList}>
                                        {Object.entries(swarmAnalyticsData[u.id].provider_usage).map(([provider, count]) => (
                                          <div key={provider} className={styles.providerItem}>
                                            <span className={styles.providerName}>{provider}</span>
                                            <span className={styles.providerCount}>{count}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {swarmAnalyticsData[u.id].agent_participation && Object.keys(swarmAnalyticsData[u.id].agent_participation).length > 0 && (
                                    <div className={styles.analyticsSection}>
                                      <IconH4 icon={RiTeamLine}>Swarm Participation</IconH4>
                                      <div className={styles.modelList}>
                                        {Object.entries(swarmAnalyticsData[u.id].agent_participation).map(([agent, stats]) => (
                                          <div
                                            key={agent}
                                            className={styles.modelItem}
                                            style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}
                                          >
                                            <span className={styles.modelName} style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                                              {agent}
                                            </span>
                                            {stats && typeof stats === 'object' ? (
                                              <>
                                                <span style={{ fontSize: '0.8em', color: 'var(--text-secondary, #888)' }}>
                                                  {stats.provider || '—'} / {stats.model || '—'}
                                                </span>
                                                <span style={{ fontSize: '0.8em' }}>
                                                  ↑ {(stats.input_tokens || 0).toLocaleString()} in &nbsp; ↓{' '}
                                                  {(stats.output_tokens || 0).toLocaleString()} out &nbsp; (
                                                  {((stats.input_tokens || 0) + (stats.output_tokens || 0)).toLocaleString()} total)
                                                </span>
                                                <span style={{ fontSize: '0.8em', color: 'var(--text-secondary, #888)' }}>
                                                  {stats.turns || 0} turns
                                                </span>
                                              </>
                                            ) : (
                                              <span className={styles.modelCount}>{stats} turns</span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}

                              {/* ── Workflow section ── */}
                              {showWorkflow && _wfData && _wfData.total_runs > 0 && (
                                <>
                                  <div className={styles.analyticsSection}>
                                    <IconH4 icon={RiStackLine}>Workflow Overview</IconH4>
                                    <div className={styles.analyticsStats}>
                                      <div className={styles.stat}>
                                        <span className={styles.statLabel}>Total Runs:</span>
                                        <span className={styles.statValue}>{_wfData.total_runs}</span>
                                      </div>
                                      <div className={styles.stat}>
                                        <span className={styles.statLabel}>Completed:</span>
                                        <span className={styles.statValue}>{_wfData.completed_runs}</span>
                                      </div>
                                      <div className={styles.stat}>
                                        <span className={styles.statLabel}>Failed:</span>
                                        <span className={styles.statValue}>{_wfData.failed_runs}</span>
                                      </div>
                                    </div>
                                  </div>

                                  {_wfData.tokens && _wfData.tokens.total > 0 && (
                                    <div className={styles.analyticsSection}>
                                      <IconH4 icon={RiStackLine}>Workflow Token Usage</IconH4>
                                      <div className={styles.analyticsStats}>
                                        <div className={styles.stat}>
                                          <span className={styles.statLabel}>Input Tokens:</span>
                                          <span className={styles.statValue}>{_wfData.tokens.input.toLocaleString()}</span>
                                        </div>
                                        <div className={styles.stat}>
                                          <span className={styles.statLabel}>Output Tokens:</span>
                                          <span className={styles.statValue}>{_wfData.tokens.output.toLocaleString()}</span>
                                        </div>
                                        <div className={styles.stat} style={{ background: 'rgba(74,158,255,0.1)', borderRadius: '4px', fontWeight: 600 }}>
                                          <span className={styles.statLabel}>Total Tokens:</span>
                                          <span className={styles.statValue}>{_wfData.tokens.total.toLocaleString()}</span>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {Object.keys(_wfData.model_usage || {}).length > 0 && (
                                    <div className={styles.analyticsSection}>
                                      <IconH4 icon={RiStackLine}>Workflow Models</IconH4>
                                      <div className={styles.modelList}>
                                        {Object.entries(_wfData.model_usage).map(([model, count]) => (
                                          <div key={model} className={styles.modelItem}>
                                            <span className={styles.modelName}>{model}</span>
                                            <span className={styles.modelCount}>{count}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {Object.keys(_wfData.provider_usage || {}).length > 0 && (
                                    <div className={styles.analyticsSection}>
                                      <IconH4 icon={RiStackLine}>Workflow Providers</IconH4>
                                      <div className={styles.providerList}>
                                        {Object.entries(_wfData.provider_usage).map(([provider, count]) => (
                                          <div key={provider} className={styles.providerItem}>
                                            <span className={styles.providerName}>{provider}</span>
                                            <span className={styles.providerCount}>{count}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                </>
                              )}

                              {/* Unified agent cards (Ask + Workflow merged per agent type) */}
                              {showAgents && agentCards.map((card) => (
                                <div key={card.key} className={styles.analyticsSection}>
                                  <IconH4 icon={RiTeamLine}>{card.label}</IconH4>
                                  <div className={styles.modelList}>
                                    <div className={styles.modelItem} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px', marginBottom: 4 }}>
                                      <span style={{ fontSize: '0.8em', fontWeight: 600 }}>
                                        ↑ {card.totalIn.toLocaleString()} in &nbsp; ↓ {card.totalOut.toLocaleString()} out &nbsp;
                                        <span style={{ color: 'var(--text-secondary, #888)' }}>
                                          ({(card.totalIn + card.totalOut).toLocaleString()} total)
                                        </span>
                                      </span>
                                      {card.askIn > 0 && (
                                        <span style={{ fontSize: '0.75em', color: 'var(--text-secondary, #888)' }}>
                                          Ask &nbsp; ↑ {card.askIn.toLocaleString()} in &nbsp; ↓ {card.askOut.toLocaleString()} out
                                        </span>
                                      )}
                                      {card.wfIn > 0 && (
                                        <span style={{ fontSize: '0.75em', color: 'var(--text-secondary, #888)' }}>
                                          Workflow &nbsp; ↑ {card.wfIn.toLocaleString()} in &nbsp; ↓ {card.wfOut.toLocaleString()} out
                                          {card.wfRuns > 0 && ` (${card.wfRuns} node run${card.wfRuns !== 1 ? 's' : ''})`}
                                        </span>
                                      )}
                                    </div>
                                    {Object.entries(card.subAgents).map(([subName, sub]) => (
                                      <div key={subName} className={styles.modelItem} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '1px', paddingLeft: 10, borderLeft: '2px solid var(--border-color, #ddd)', marginLeft: 4, marginTop: 4 }}>
                                        <span className={styles.modelName} style={{ textTransform: 'capitalize' }}>{subName}</span>
                                        <span style={{ fontSize: '0.8em', color: 'var(--text-secondary, #888)' }}>
                                          {sub.provider || '—'} / {sub.model || '—'}
                                        </span>
                                        <span style={{ fontSize: '0.8em' }}>
                                          ↑ {(sub.input_tokens || 0).toLocaleString()} in &nbsp; ↓ {(sub.output_tokens || 0).toLocaleString()} out
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}

                              {showWorkflow && (!_wfData || _wfData.total_runs === 0) && (
                                <div className={styles.analyticsSection}>
                                  <IconH4 icon={RiStackLine}>Workflow</IconH4>
                                  <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #888)', margin: 0 }}>
                                    No workflow runs recorded for this user.
                                  </p>
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className={styles.analyticsError}>No usage data available</div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>

      {/* Profile detail popup */}
      {profilePopup && (
        <div
          onClick={() => setProfilePopup(null)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 1000, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card-background, #1e1e2e)',
              border: '1px solid var(--border-color, #333)',
              borderRadius: '10px', padding: '28px 32px',
              minWidth: '320px', maxWidth: '480px', width: '90%',
              boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setProfilePopup(null)}
              style={{
                position: 'absolute', top: '12px', right: '14px',
                background: 'none', border: 'none', fontSize: '1.3rem',
                cursor: 'pointer', color: 'var(--text-secondary, #888)',
                lineHeight: 1,
              }}
              title="Close"
            >×</button>

            <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem' }}>
              {profilePopup.name || 'Unknown'}
            </h3>
            <div style={{ marginBottom: '16px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {!!profilePopup.is_admin && <span className={styles.adminBadge}>Admin</span>}
              {profilePopup.id === user.id && <span className={styles.youBadge}>You</span>}
              <span className={profilePopup.approved ? styles.statusApproved : styles.statusPending}>
                {profilePopup.approved ? 'Approved' : 'Pending'}
              </span>
            </div>

            {(() => {
              const expParts = [
                profilePopup.exp_ml > 0 && `ML: ${profilePopup.exp_ml}`,
                profilePopup.exp_dl > 0 && `DL: ${profilePopup.exp_dl}`,
                profilePopup.exp_llm > 0 && `LLM: ${profilePopup.exp_llm}`,
                profilePopup.exp_vlm > 0 && `VLM: ${profilePopup.exp_vlm}`,
                profilePopup.exp_agentic > 0 && `Agentic: ${profilePopup.exp_agentic}`,
              ].filter(Boolean).join(' · ');
              return [
                ['Email', profilePopup.email],
                ['Job Title', profilePopup.job_title],
                ['Company', profilePopup.company_affiliate],
                ['Experience', expParts || null],
                ['Referred By', profilePopup.referred_by],
                ['Member Since', profilePopup.created_at ? new Date(profilePopup.created_at).toLocaleDateString() : null],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', gap: '8px', marginBottom: '8px', fontSize: '0.9rem' }}>
                  <span style={{ color: 'var(--text-secondary, #888)', minWidth: '100px', flexShrink: 0 }}>{label}:</span>
                  <span style={{ wordBreak: 'break-word' }}>{value || '—'}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </>
  );
};

export default AdminTable;
